import { useMutation } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { Dispatch, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { passwordReset } from '@/api/user/passwordReset';
import Button from '@/components/UI/Button';
import { EMAIL_REGEX } from '@/utils/regex';

import { TextField } from '../UI/TextField';
import { AuthFormType, ForgotPassword } from './Authform.type';

interface ForgotPasswordFormProps {
  onSuccessCallback: () => void;
  setSelectedForm: Dispatch<SetStateAction<AuthFormType>>;
  aditionalContent?: React.ReactNode;
}

export default function ForgotPasswordForm({
  onSuccessCallback,
  setSelectedForm,
  aditionalContent,
}: ForgotPasswordFormProps) {
  const {
    register,
    handleSubmit,
    control,
    formState: { isValid, errors },
  } = useForm<ForgotPassword>({ mode: 'onBlur' });
  const tErrors = useTranslations('Errors.Form');
  const t = useTranslations('Components.Form.ForgotPasswordForm');
  const passwordResetMutation = useMutation(passwordReset);

  const onSubmit = async (formdata: ForgotPassword) => {
    await passwordResetMutation.mutateAsync(
      { email: formdata.email },
      {
        onSuccess() {
          toast.success(t('success'), { duration: 4000 });
        },
        onError() {
          toast.error(t('errorOccured'));
        },
      }
    );
  };

  return (
    <form className='mb-10 space-y-4' onSubmit={handleSubmit(onSubmit)}>
      {aditionalContent}
      <TextField
        {...register('email', {
          required: true,
          pattern: {
            value: EMAIL_REGEX,
            message: tErrors('invalidEmail'),
          },
        })}
        label={t('email')}
        placeholder=''
        error={errors.email}
      />
      <p>{t('forgotPassword')}</p>
      <Button
        width='full'
        disabled={
          !isValid ||
          passwordResetMutation.isLoading ||
          passwordResetMutation.isSuccess
        }
      >
        {t('sendLink')}
      </Button>
      <Button onClick={() => setSelectedForm('login')} variant='link'>
        {t('goBackToLogin')}
      </Button>
    </form>
  );
}
